import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  LayersControl,
  Marker,
  Popup,
  ImageOverlay,
  Rectangle,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useDispatch, useSelector } from "react-redux";
import {
  addArea,
  clearAllMapState,
  deleteArea,
  setFileContent,
  setUploaderFlag,
} from "../../reducers/MapSlice";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Icon } from "leaflet";
import { DraggableMarker } from "./Draggable";

// Custom hook to handle clearing drawn shapes
function useShapeClearing() {
  const mapRef = useRef();
  
  const clearAllShapes = () => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.eachLayer((layer) => {
        if (
          layer instanceof L.Polygon ||
          layer instanceof L.GeoJSON ||
          layer instanceof L.Circle ||
          layer instanceof L.Rectangle
        ) {
          map.removeLayer(layer);
        }
      });
    }
  };

  useEffect(() => {
    const handleClearShapes = () => {
      clearAllShapes();
    };

    // Listen for the custom event from CoordinatesInput
    window.addEventListener('clearDrawnShapes', handleClearShapes);

    return () => {
      window.removeEventListener('clearDrawnShapes', handleClearShapes);
    };
  }, []);

  return { mapRef, clearAllShapes };
}

// Component to access map instance and register it with the clearing hook
function MapInstanceRegistrar({ onMapReady }) {
  const map = useMap();
  
  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

function Map(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const center = { lat: 19.55979, lng: 29.267578 };
  const { mapRef, clearAllShapes } = useShapeClearing();

  let fileContent = useSelector((state) => state.mapState.uploadedFileContent);
  if (fileContent === "") {
    fileContent = JSON.parse(props.mapFile);
  }
  const uploaderFlag = useSelector((state) => state.mapState.uploaderFlag);
  const [deleteState, setDelete] = useState(false);

  const _onCreated = (e) => {
    let layer = e.layer;
    dispatch(
      addArea({
        polyId: layer._leaflet_id,
        coordinates: layer.toGeoJSON().geometry.coordinates[0],
      })
    );
  };

  const _onDeleted = (e) => {
    const deletedKeys = Object.keys(e.layers._layers);
    dispatch(deleteArea(deletedKeys));
  };

  const handleMapReady = (map) => {
    mapRef.current = map;
  };

  function DeleteGeoJson() {
    const map = useMap();
    map.eachLayer(function (layer) {
      if (layer instanceof L.Polygon) {
        layer.remove();
      }
    });
    localStorage.removeItem("mapFileContent");
    dispatch(setUploaderFlag(false));
    setDelete(false);
    dispatch(clearAllMapState());
  }

  function AddGeoJson() {
    const map = useMap();

    // Clear existing GeoJSON layers first
    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON) {
        map.removeLayer(layer);
      }
    });

    try {
      // Validate fileContent
      if (!fileContent || typeof fileContent !== "object") {
        console.error("Invalid fileContent:", fileContent);
        toast.error("Invalid file content");
        return null;
      }

      // Create GeoJSON layer with error handling
      const geoJsonLayer = L.geoJSON(fileContent, {
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            layer.bindPopup(
              Object.entries(feature.properties)
                .map(([key, value]) => `${key}: ${value}`)
                .join("<br>")
            );
          }
        },
      });

      if (!geoJsonLayer) {
        throw new Error("Failed to create GeoJSON layer");
      }

      // Add to map and get bounds
      geoJsonLayer.addTo(map);
      const bounds = geoJsonLayer.getBounds();

      if (!bounds.isValid()) {
        throw new Error("Invalid bounds");
      }

      const boundList = convertToBounds(bounds);
      dispatch(
        addArea({
          polyId: geoJsonLayer._leaflet_id,
          coordinates: fileContent,
        })
      );
      dispatch(setFileContent(""));

      // Fit map to bounds
      map.fitBounds(bounds);

      return null;
    } catch (error) {
      console.error("Error in AddGeoJson:", error);
      toast.error("Failed to display GeoJSON on map");
      dispatch(setFileContent(""));
      return null;
    }
  }

  function deleteUploadedFiles() {
    localStorage.removeItem("mapFileContent");
    dispatch(clearAllMapState());
    dispatch(setUploaderFlag(false));
    setDelete(true);
  }

  function convertToBounds(obj) {
    const { _southWest, _northEast } = obj;

    const west = _southWest.lng;
    const east = _northEast.lng;
    const north = _northEast.lat;
    const south = _southWest.lat;

    return [
      [west, south],
      [west, north],
      [east, north],
      [east, south],
    ];
  }

  const ArabWorldBounds = {
    southWest: {
      lat: -18.454445427710418,
      lng: -18.675611088484246,
    },
    northEast: {
      lat: 45.261019159784439,
      lng: 60.756334763925977,
    },
  };

  const bounds = L.latLngBounds(
    ArabWorldBounds.southWest,
    ArabWorldBounds.northEast
  );

  const areas = useSelector((state) => state.mapState.areas);

  return (
    <>
      <MapContainer
        bounds={bounds}
        maxBounds={bounds}
        center={center}
        minZoom={4}
      >
        <MapInstanceRegistrar onMapReady={handleMapReady} />
        
        {areas &&
          areas.map(
            (area, index) =>
              (area.imgurl || area.img64) &&
              area.bbox && (
                <ImageOverlay
                  key={index}
                  url={
                    area.imgurl ||
                    (area.img64 && `data:image/png;base64,${area.img64}`)
                  }
                  bounds={area.bbox}
                />
              )
          )}
        {fileContent && <AddGeoJson />}
        {deleteState && <DeleteGeoJson />}
        <TileLayer
          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />

        <FeatureGroup>
          <EditControl
            position="topleft"
            onCreated={_onCreated}
            onDeleted={_onDeleted}
            onDrawStart={(e) => {
              // Clear existing map state and files when starting to draw
              localStorage.removeItem("mapFileContent");
              dispatch(clearAllMapState());
              dispatch(setUploaderFlag(false));
              setDelete(true);

              const map = e.target;
              // Clear existing drawn shapes but not the one being drawn
              map.eachLayer((layer) => {
                if (
                  layer instanceof L.Polygon ||
                  layer instanceof L.GeoJSON ||
                  layer instanceof L.Circle ||
                  layer instanceof L.Rectangle
                ) {
                  map.removeLayer(layer);
                }
              });
            }}
            draw={{
              rectangle: true,
              circle: true,
              polyline: false,
              marker: false,
              polygon: true,
              circlemarker: false,
            }}
          />
        </FeatureGroup>
      </MapContainer>
      {uploaderFlag ? (
        <div className="remove-area">
          <button
            className="delete-button"
            onClick={() => deleteUploadedFiles()}
          >
            {t("Map.remove_file")}
          </button>
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
}

export default Map;